// src/components/Users.jsx

import React, { useState, useEffect } from 'react';
import { firestore, auth } from '../../firebase';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import './Users.css';
import Spinner from '../Common/Spinner';
import UsersHeader from './UsersHeader';
import UserTable from './UserTable';
import UserForm from './UserForm';
import DeleteConfirmationModal from '../Common/DeleteConfirmationModal';
import ResetPasswordConfirmationModal from '../Common/ResetPasswordConfirmationModal';
import CustomModal from '../Common/CustomModal';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next'; // Import the i18n hook
import { useLanguageSettings } from '../Common/LanguageSettingsContext'; // Import LanguageSettings hook
import { useColorSettings } from '../Common/ColorSettingsContext'; // Import ColorSettings hook

const Users = ({ showToast }) => {
  const { t } = useTranslation(); // Initialize the i18n translation hook
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('user');
  const [fullName, setFullName] = useState('');
  const [editingUser, setEditingUser] = useState(null);

  // State to check if the view is mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Consume LanguageSettings and ColorSettings contexts
  const { language, updateLanguage, loading: languageLoading } = useLanguageSettings();
  const { appSettings, loading: colorLoading } = useColorSettings();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersCollection = await getDocs(collection(firestore, 'roles'));
        const usersList = usersCollection.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(usersList);
      } catch (error) {
        console.error('Error fetching users: ', error);
        showToast(t('users.fetchError'), 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [showToast, t]);

  // Function to handle adding a new user
  const handleAddUser = () => {
    setModalTitle(t('users.addUser'));
    setEmail('');
    setRole('user');
    setFullName('');
    setEditingUser(null);
    setShowUserModal(true);
  };

  // Function to handle editing an existing user
  const handleEditUser = (user) => {
    setModalTitle(t('users.editUser'));
    setEmail(user.email);
    setRole(user.role);
    setFullName(user.fullName || '');
    setEditingUser(user);
    setShowUserModal(true);
  };

  // Function to handle deleting a user
  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  // Function to confirm deletion
  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        const idToken = await currentUser.getIdToken();
        const response = await fetch('https://delete-user-xv2ujygbla-uc.a.run.app', {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
          body: JSON.stringify({ uid: userToDelete.id }),
        });

        const data = await response.json();

        if (response.ok) {
          setUsers(users.filter((user) => user.id !== userToDelete.id));
          showToast(t('users.deleteSuccess'), 'success');
        } else {
          showToast(`${t('users.deleteError')}: ${data.message || data}`, 'error');
        }
      } else {
        showToast(t('users.notAuthenticated'), 'error');
      }
    } catch (error) {
      console.error('Error deleting user: ', error);
      showToast(t('users.deleteError'), 'error');
    } finally {
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  // Function to handle password reset
  const handleResetPassword = (user) => {
    setUserToReset(user);
    setShowResetModal(true);
  };

  // Function to confirm password reset
  const confirmResetPassword = () => {
    if (!userToReset) return;

    auth
      .sendPasswordResetEmail(userToReset.email)
      .then(() => {
        showToast(t('users.resetPasswordSuccess'), 'success');
      })
      .catch((error) => {
        console.error('Error sending password reset email: ', error);
        showToast(t('users.resetPasswordError'), 'error');
      })
      .finally(() => {
        setShowResetModal(false);
        setUserToReset(null);
      });
  };

  // Function to handle form submission for adding/editing user
  const handleUserModalSubmit = async (e) => {
    e.preventDefault();

    if (!fullName.trim()) {
      showToast(t('users.fullNameRequired'), 'error');
      return;
    }

    if (!editingUser) {
      if (!email) {
        showToast(t('users.emailRequired'), 'error');
        return;
      }

      try {
        const currentUser = auth.currentUser;
        if (currentUser) {
          const idToken = await currentUser.getIdToken();

          const response = await fetch('https://create-user-xv2ujygbla-uc.a.run.app', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`,
            },
            body: JSON.stringify({ email, role, fullName }),
          });

          const data = await response.json();

          if (response.ok) {
            setUsers([...users, { id: data.uid, email, role, fullName }]);
            showToast(t('users.addSuccess'), 'success');
            setShowUserModal(false);
          } else {
            showToast(`${t('users.addError')}: ${data.message || data}`, 'error');
          }
        } else {
          showToast(t('users.notAuthenticated'), 'error');
        }
      } catch (error) {
        console.error('Error saving user: ', error);
        showToast(t('users.addError'), 'error');
      }
    } else {
      try {
        const userDocRef = doc(firestore, 'roles', editingUser.id);
        await updateDoc(userDocRef, {
          fullName: fullName,
          role: role,
        });

        setUsers(
          users.map((user) =>
            user.id === editingUser.id ? { ...user, fullName, role } : user
          )
        );
        showToast(t('users.updateSuccess'), 'success');
        setShowUserModal(false);
        setEditingUser(null);
      } catch (error) {
        console.error('Error updating user: ', error);
        showToast(t('users.updateError'), 'error');
      }
    }
  };

  // State Variables for Delete Confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // State Variables for Reset Password Confirmation
  const [showResetModal, setShowResetModal] = useState(false);
  const [userToReset, setUserToReset] = useState(null);

  return (
    <div className="users-table-container">
      {/* Spinner Overlay */}
      {isLoading && <Spinner />}

      {/* Header Section */}
      <UsersHeader onAddUser={handleAddUser} isMobile={isMobile} />

      {!isLoading && (
        <UserTable
          users={users}
          isMobile={isMobile}
          onEditUser={handleEditUser}
          onDeleteUser={handleDeleteUser}
          onResetPassword={handleResetPassword}
        />
      )}

      {/* User Modal for Adding/Editing Users */}
      {showUserModal && (
        <CustomModal
          show={showUserModal}
          onClose={() => setShowUserModal(false)}
          title={modalTitle}
        >
          <UserForm
            fullName={fullName}
            setFullName={setFullName}
            email={email}
            setEmail={setEmail}
            role={role}
            setRole={setRole}
            isMobile={isMobile}
            onSubmit={handleUserModalSubmit}
            isEditing={editingUser !== null}
          />
        </CustomModal>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <DeleteConfirmationModal
          show={showDeleteModal}
          onConfirm={confirmDeleteUser}
          onCancel={() => setShowDeleteModal(false)}
          record={userToDelete}
          isMobile={isMobile}
        />
      )}

      {/* Reset Password Confirmation Modal */}
      {showResetModal && (
        <ResetPasswordConfirmationModal
          show={showResetModal}
          onConfirm={confirmResetPassword}
          onCancel={() => setShowResetModal(false)}
          record={userToReset}
          isMobile={isMobile}
        />
      )}
    </div>
  );
};

Users.propTypes = {
  showToast: PropTypes.func.isRequired,
};

export default Users;
